import { get } from "lodash-es";
import { DateTime } from "luxon";
import posthog from "posthog-js";
import React, { FC, useState } from "react";
import { NavLink, useParams } from "react-router-dom";

import { Card, CardHeader, Stack, SvgIcon } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@mui/x-date-pickers";

import HelpInfo from "../../components/HelpInfo";
import Page from "../../components/Page";
import { ConfirmationDialog } from "../../components/confirmation-dialog";
import { DeleteIcon, ViewIcon } from "../../components/icons";
import { PropertyList } from "../../components/property-list";
import { PropertyListItem } from "../../components/property-list-item";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { useSelector } from "../../store";
import {
  useDeleteServiceAccountMutation,
  useGetClusterQuery,
  useGetServiceAccountQuery,
  useGetServiceAccountTokenQuery,
} from "../../store/pomeriumApi";
import { DisplayJWT } from "../settings/components/DisplayJWT";

const ViewServiceAccount: FC = () => {
  const { serviceAccountId } = useParams();
  const navigate = useNavigateWithSearch();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [deleteAccount] = useDeleteServiceAccountMutation();
  const { data: serviceAccount } = useGetServiceAccountQuery(
    {
      organizationId,
      clusterId,
      serviceAccountId: serviceAccountId || "",
    },
    { skip: !organizationId || !clusterId || !serviceAccountId },
  );
  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );
  const { byId: orgById } = useSelector((state) => state.organizations);
  const readOnly = get(orgById, organizationId + ".readOnly", false) as boolean;

  const { data: serviceAccountToken } = useGetServiceAccountTokenQuery(
    {
      organizationId,
      clusterId,
      serviceAccountId: serviceAccountId || "",
    },
    { skip: !organizationId || !clusterId || !serviceAccountId || !showToken },
  );

  const onConfirmDelete = async () => {
    await deleteAccount({
      organizationId,
      serviceAccountId: serviceAccount?.id || "",
      clusterId,
    });
    posthog.capture("service_account_deleted", {
      deleted: serviceAccount?.id || "",
    });
    navigate("/app/management/service-accounts");
  };

  return (
    <Page title="Service Account Details">
      <Stack spacing={4}>
        <Stack alignItems="flex-start" spacing={1}>
          <Button
            color="primary"
            component={NavLink}
            sx={{ mr: 2 }}
            to={{
              pathname: `/app/management/service-accounts`,
            }}
            startIcon={
              <SvgIcon fontSize="small">
                <ArrowLeftIcon />
              </SvgIcon>
            }
            variant="text"
          >
            Service Accounts
          </Button>
        </Stack>
      </Stack>
      <Stack sx={{ ml: 1, mt: 4 }} spacing={4}>
        <Stack
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h4">
            {serviceAccount?.userId}@{cluster?.fqdn || ""}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Divider />
        </Stack>

        <Stack direction="row" gap={2}>
          <Card sx={{ minWidth: "60%" }}>
            <CardHeader title="Properties" />
            <Divider />
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              sx={{
                "& > *": {
                  width: {
                    md: "50%",
                  },
                },
              }}
            >
              <PropertyList>
                <PropertyListItem
                  label="Description"
                  value={serviceAccount?.description || ""}
                />
                <PropertyListItem
                  label="User Id"
                  value={serviceAccount?.userId || ""}
                />
              </PropertyList>
              <PropertyList>
                <PropertyListItem
                  label="Expires"
                  value={
                    serviceAccount?.expiresAt
                      ? DateTime.fromISO(
                          serviceAccount.expiresAt,
                        ).toLocaleString(DateTime.DATETIME_MED)
                      : "never"
                  }
                />
                <PropertyListItem
                  label="Created"
                  value={
                    serviceAccount?.createdAt
                      ? DateTime.fromISO(
                          serviceAccount.createdAt,
                        ).toLocaleString(DateTime.DATETIME_MED)
                      : ""
                  }
                />
                <PropertyListItem
                  label="Updated"
                  value={
                    serviceAccount?.updatedAt
                      ? DateTime.fromISO(
                          serviceAccount.updatedAt,
                        ).toLocaleString(DateTime.DATETIME_MED)
                      : ""
                  }
                />
              </PropertyList>
            </Stack>
          </Card>

          <Card sx={{ minWidth: "38%" }}>
            <CardHeader title="Actions" />
            <Divider />
            <Stack justifyContent={"flex-start"} sx={{ pl: 2, pt: 2 }}>
              <Button
                onClick={() => setConfirmDelete(true)}
                sx={{ width: 120, justifyContent: "flex-start" }}
                startIcon={
                  <SvgIcon fontSize="small">
                    <DeleteIcon />
                  </SvgIcon>
                }
                variant="text"
                disabled={readOnly}
              >
                Delete
              </Button>
              <Button
                onClick={() => setShowToken(true)}
                sx={{ width: 120, justifyContent: "flex-start" }}
                startIcon={
                  <SvgIcon fontSize="small">
                    <ViewIcon />
                  </SvgIcon>
                }
                variant="text"
              >
                View Token
              </Button>
            </Stack>
          </Card>
        </Stack>
      </Stack>
      <ConfirmationDialog
        title="Delete service account?"
        message="Are you sure you want to delete this service account? This can't be undone."
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onConfirmDelete}
        variant="error"
      />
      <DisplayJWT
        open={showToken}
        jwt={serviceAccountToken?.token || ""}
        onClose={() => setShowToken(false)}
        title={
          <>
            Service account token <HelpInfo docsKey="service-account-token" />
          </>
        }
        message="Here is your current token:"
      />
    </Page>
  );
};

export default ViewServiceAccount;
